import { isEmpty, isNil, map, omitBy, snakeCase } from 'lodash'
import moment from 'moment'

import {
  ReminderAction,
  ReminderFrequency,
  ReminderMode,
  ReminderTrigger,
  ReminderTriggerUnit,
} from '~shared/types'

import {
  ReminderActionType,
  ReminderDayOfWeekType,
  ReminderEventType,
  ReminderFieldSchema,
  ReminderUnitType,
} from '~templates/Field'

import { DaysInWeek } from './RecurringReminder'

export const getFrequencyUnit = (frequency?: ReminderFrequency) => {
  if (frequency === ReminderFrequency.Weekly) {
    return ReminderUnitType.Week
  }
  if (frequency === ReminderFrequency.Monthly) {
    return ReminderUnitType.Month
  }
  if (frequency === ReminderFrequency.Daily) {
    return ReminderUnitType.Day
  }
  return undefined
}

export const getTriggerUnit = (triggerUnit?: ReminderTriggerUnit) => {
  if (triggerUnit === ReminderTriggerUnit.Month) {
    return ReminderUnitType.Month
  }
  if (triggerUnit === ReminderTriggerUnit.Week) {
    return ReminderUnitType.Week
  }
  if (triggerUnit === ReminderTriggerUnit.Day) {
    return ReminderUnitType.Day
  }
  return undefined
}
export const getTriggerEvent = (triggerEvent?: ReminderTrigger) => {
  if (triggerEvent === ReminderTrigger.Enrollment) {
    return ReminderEventType.Enrollment
  }
  if (triggerEvent === ReminderTrigger.Exact) {
    return ReminderEventType.Exact
  }
  if (triggerEvent === ReminderTrigger.Discharge) {
    return ReminderEventType.Discharge
  }
  if (triggerEvent === ReminderTrigger.Never) {
    return ReminderEventType.Never
  }
  if (triggerEvent === ReminderTrigger.ProgramEnrollment) {
    return ReminderEventType.ProgramEnrollment
  }
  if (triggerEvent === ReminderTrigger.ProgramDischarge) {
    return ReminderEventType.ProgramDischarge
  }
  return undefined
}

export const getAction = (action: ReminderAction) => {
  if (action === ReminderAction.Discharge) {
    return ReminderActionType.Discharge
  }
  if (action === ReminderAction.SendReminder) {
    return ReminderActionType.SendReminder
  }
  return ReminderActionType.SendTemplate
}

export const getDay = (day: string) => {
  if (day === DaysInWeek.Monday) {
    return ReminderDayOfWeekType.Mon
  }
  if (day === DaysInWeek.Tuesday) {
    return ReminderDayOfWeekType.Tue
  }
  if (day === DaysInWeek.Wednesday) {
    return ReminderDayOfWeekType.Wed
  }
  if (day === DaysInWeek.Thursday) {
    return ReminderDayOfWeekType.Thu
  }
  if (day === DaysInWeek.Friday) {
    return ReminderDayOfWeekType.Fri
  }
  if (day === DaysInWeek.Saturday) {
    return ReminderDayOfWeekType.Sat
  }
  if (day === DaysInWeek.Sunday) {
    return ReminderDayOfWeekType.Sun
  }
  return undefined
}

export const createReminderObj = ({
  reminderOptions,
  startTriggerDate = null,
  endTriggerDate = null,
  reminderTime = '00:00',
  selectedDay = [],
}: {
  reminderOptions: ReminderFieldSchema['reminderOptions']
  startTriggerDate?: Date | null
  endTriggerDate?: Date | null
  reminderTime?: string
  selectedDay?: string[]
}) => {
  const purpose = {
    purpose: snakeCase(reminderOptions.messageTemplatePurpose),
    template_set: reminderOptions.messageTemplate,
  }
  return {
    schedule: {
      action: {
        type: getAction(reminderOptions.action),
        [snakeCase(reminderOptions.action)]:
          reminderOptions.action !== ReminderAction.SendTemplate ? {} : purpose,
      },
      ...omitBy(
        {
          start_trigger: omitBy(
            {
              event: getTriggerEvent(reminderOptions.startTrigger),
              time_of_day: reminderOptions.startTriggerTime,
              duration: reminderOptions.startTriggerDuration,
              unit: getTriggerUnit(reminderOptions.startTriggerUnit),
              is_after: reminderOptions.startTriggerIsAfter,
              exact_trigger_on: startTriggerDate
                ? moment(startTriggerDate).format()
                : null,
            },
            isNil,
          ),
          end_trigger: omitBy(
            {
              event: getTriggerEvent(reminderOptions.endTrigger),
              time_of_day: reminderOptions.endTriggerTime,
              duration: reminderOptions.endTriggerDuration,
              unit: getTriggerUnit(reminderOptions.endTriggerUnit),
              is_after: reminderOptions.endTriggerIsAfter,
              exact_trigger_on: endTriggerDate
                ? moment(endTriggerDate).format()
                : null,
            },
            isNil,
          ),
          recurring_frequencies:
            getFrequencyUnit(reminderOptions.frequency) ===
              ReminderUnitType.Day &&
            reminderOptions.mode === ReminderMode.Recurring
              ? [
                  omitBy(
                    {
                      every: reminderOptions.frequencyDuration,
                      time_of_day: reminderTime,
                      unit: getFrequencyUnit(reminderOptions.frequency),
                    },
                    isNil,
                  ),
                ]
              : map(selectedDay, (day) =>
                  omitBy(
                    {
                      every: reminderOptions.frequencyDuration,
                      time_of_day: reminderTime,
                      unit: getFrequencyUnit(reminderOptions.frequency),
                      day_of_week: getDay(day),
                      day_of_month:
                        reminderOptions.frequency === ReminderFrequency.Monthly
                          ? parseInt(day)
                          : undefined,
                    },
                    isNil,
                  ),
                ),
        },
        (v, k) => k !== 'action' && isEmpty(v),
      ),
    },
  }
}
